import React, { useEffect, useState } from "react";
import { api } from "../api/alphaClient";
import CandlestickChart from "../components/CandlestickChart";
import { BookOpen, Calendar, BarChart2, Layers } from "lucide-react";

const CHART_START_DATE = "2025-06-01";

export default function TradeBook({ universe }) {
  const [strategies, setStrategies] = useState([]);
  const [strategiesStatus, setStrategiesStatus] = useState("loading");
  const [strategiesError, setStrategiesError] = useState("");
  const [strategyId, setStrategyId] = useState(null);

  const [weeks, setWeeks] = useState([]);
  const [weeksStatus, setWeeksStatus] = useState("idle");
  const [weeksError, setWeeksError] = useState("");

  const [selectedScrip, setSelectedScrip] = useState(null);
  const [candles, setCandles] = useState([]);
  const [ohlcvStatus, setOhlcvStatus] = useState("idle");
  const [ohlcvError, setOhlcvError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setStrategiesStatus("loading");
    setStrategyId(null);
    api.listStrategies(universe)
      .then((data) => {
        if (cancelled) return;
        const list = data.strategies || [];
        setStrategies(list);
        setStrategiesStatus("ok");
        const live = list.find((s) => s.is_live);
        setStrategyId((live || list[0])?.strategy_id ?? null);
      })
      .catch((err) => {
        if (!cancelled) { setStrategiesStatus("error"); setStrategiesError(err.message || "Failed to load strategies."); }
      });
    return () => { cancelled = true; };
  }, [universe]);

  useEffect(() => {
    if (strategyId == null) return;
    let cancelled = false;
    setWeeksStatus("loading");
    api.getStrategyBaskets(strategyId, universe)
      .then((data) => {
        if (cancelled) return;
        setWeeks(data.weeks || []);
        setWeeksStatus("ok");
      })
      .catch((err) => {
        if (!cancelled) { setWeeksStatus("error"); setWeeksError(err.message || "Failed to load baskets."); }
      });
    return () => { cancelled = true; };
  }, [strategyId, universe]);

  useEffect(() => {
    setSelectedScrip(null);
  }, [strategyId, universe]);

  useEffect(() => {
    if (!selectedScrip) { setCandles([]); return; }
    let cancelled = false;
    setOhlcvStatus("loading");
    const endDate = new Date().toISOString().slice(0, 10);
    api.getScripOhlcv(selectedScrip, { startDate: CHART_START_DATE, endDate })
      .then((data) => {
        if (cancelled) return;
        setCandles(data.candles || []);
        setOhlcvStatus("ok");
      })
      .catch((err) => {
        if (!cancelled) { setOhlcvStatus("error"); setOhlcvError(err.message || "Failed to load chart data."); }
      });
    return () => { cancelled = true; };
  }, [selectedScrip]);

  const activeStrategyObj = strategies.find((s) => s.strategy_id === strategyId);

  return (
    <div className="flex flex-col gap-6 h-full overflow-y-auto pr-1">
      {/* Strategy Variant Selector Bar */}
      <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-4 shadow-lg flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-100 uppercase tracking-wider flex items-center gap-2">
            <Layers className="w-4 h-4 text-blue-400" />
            Model Variants ({universe})
          </h3>
          <span className="text-xs font-mono text-gray-400">
            Selected: <span className="text-blue-400 font-bold">{activeStrategyObj?.variant_code || "N/A"}</span>
          </span>
        </div>

        {strategiesStatus === "loading" ? (
          <div className="py-3 text-xs font-mono text-gray-500">Loading model variants...</div>
        ) : (
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin">
            {strategies.map((s) => (
              <button
                key={s.strategy_id}
                onClick={() => setStrategyId(s.strategy_id)}
                className={`px-3 py-1.5 text-xs font-mono rounded-lg border transition-all shrink-0 cursor-pointer ${
                  strategyId === s.strategy_id
                    ? "bg-blue-600 text-white font-bold border-blue-500 shadow-md"
                    : "bg-[#14171f] text-gray-400 hover:text-gray-200 border-gray-800"
                }`}
              >
                {s.variant_code}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Weekly Selection Baskets Card */}
      <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-5 shadow-lg flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 border-b border-gray-800/80 pb-3">
          <h3 className="text-sm font-bold text-gray-100 uppercase tracking-wider flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-amber-400" />
            Weekly Baskets History ({activeStrategyObj?.variant_code})
          </h3>
          <span className="text-xs font-mono text-gray-400">
            Click any scrip badge below to render its candlestick chart
          </span>
        </div>

        {weeksStatus === "loading" ? (
          <div className="p-8 text-center text-xs font-mono text-gray-500">Loading weekly baskets history...</div>
        ) : weeksStatus === "error" ? (
          <div className="p-6 text-center text-xs font-mono text-rose-400">{weeksError}</div>
        ) : weeks.length === 0 ? (
          <div className="p-8 text-center text-xs font-mono text-gray-500">No weekly selections found for this model.</div>
        ) : (
          <div className="max-h-[380px] overflow-y-auto border border-gray-800 rounded-lg bg-[#14171f] divide-y divide-gray-800/60 p-2">
            {weeks.map((w, idx) => (
              <div key={idx} className="py-3 px-3 flex flex-col sm:flex-row sm:items-start gap-3 hover:bg-gray-800/40 transition-colors rounded-md">
                <div className="flex items-center gap-1.5 text-xs font-mono font-bold text-gray-300 w-32 shrink-0 pt-1">
                  <Calendar className="w-3.5 h-3.5 text-blue-400" />
                  {w.week || w.selection_date}
                </div>
                <div className="flex flex-wrap gap-1.5 flex-1 max-h-[110px] overflow-y-auto">
                  {(w.scrips || []).map((scrip) => {
                    const isSelected = selectedScrip === scrip;
                    return (
                      <button
                        key={scrip}
                        onClick={() => setSelectedScrip(scrip)}
                        className={`px-2.5 py-1 rounded text-xs font-mono transition-all cursor-pointer border ${
                          isSelected
                            ? "bg-blue-600 text-white font-bold border-blue-400 shadow-md"
                            : "bg-[#0f1116] hover:bg-gray-800 text-gray-300 border-gray-800"
                        }`}
                      >
                        {scrip}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Candlestick Chart Box */}
      <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-5 shadow-lg flex flex-col gap-3 min-h-[440px] mb-6">
        <div className="flex items-center justify-between border-b border-gray-800/80 pb-3">
          <div className="flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-bold text-gray-100 uppercase tracking-wider">
              {selectedScrip ? `${selectedScrip} — Daily Candlestick Chart` : "Select a Scrip to View Candlestick Chart"}
            </h3>
          </div>
          {selectedScrip && (
            <button
              onClick={() => setSelectedScrip(null)}
              className="px-2.5 py-1 text-xs font-semibold bg-[#14171f] hover:bg-gray-800 text-gray-400 hover:text-gray-200 border border-gray-700 rounded-md transition-colors"
            >
              Close Chart
            </button>
          )}
        </div>

        <div className="flex-1 min-h-[360px] w-full flex items-center justify-center">
          {!selectedScrip ? (
            <div className="text-center text-xs font-mono text-gray-500 p-8">
              Click on any scrip badge in the weekly baskets above to open its interactive candlestick chart.
            </div>
          ) : ohlcvStatus === "loading" ? (
            <div className="text-center text-xs font-mono text-gray-400 flex flex-col items-center gap-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-400"></div>
              Loading candlestick data for {selectedScrip}...
            </div>
          ) : ohlcvStatus === "error" ? (
            <div className="text-center text-xs font-mono text-rose-400 p-4">{ohlcvError}</div>
          ) : (
            <CandlestickChart data={candles} />
          )}
        </div>
      </div>
    </div>
  );
}
